import React from "react";
import PropTypes from "prop-types";

/**
 * VersionStrip renders the service versions running for the selected
 * organization, as a row of labelled badges above the Org-first panes.
 *
 * The data comes from the tenant DB's release_versions table, which an
 * operator curates and the release-version-sync controller keeps current.
 * A tenant that does not report versions is a normal state, not an error,
 * so this renders a muted note rather than an alert in that case and the
 * component never hides the rest of the tab.
 */

function formatUpdated(iso) {
  if (!iso) return "never recorded";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "unknown";
  return d.toLocaleString();
}

function VersionStrip({ data, loading, error }) {
  if (loading) {
    return (
      <div className="card shadow-sm mb-3">
        <div className="card-body py-2 text-muted small">Loading service versions…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card shadow-sm mb-3">
        <div className="card-body py-2 text-danger small">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const versions = data.versions || [];

  return (
    <div className="card shadow-sm mb-3">
      <div className="card-header py-2 d-flex justify-content-between align-items-center">
        <span>Service Versions — {data.org_name}</span>
      </div>
      <div className="card-body py-2">
        {!data.available ? (
          <span className="text-muted small">
            {data.error || "No service versions reported for this organization."}
          </span>
        ) : versions.length === 0 ? (
          <span className="text-muted small">
            No services are tracked for this organization.
          </span>
        ) : (
          <div className="d-flex flex-wrap gap-2">
            {versions.map((v) => (
              <span
                key={v.service_name}
                className="badge bg-light text-dark border d-inline-flex align-items-center gap-1 py-2 px-2"
                title={`${v.service_name} — last changed ${formatUpdated(v.updated_on)}`}
              >
                <span className="text-muted fw-normal">{v.display_name}</span>
                <span className="fw-semibold">{v.image_tag || "—"}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

VersionStrip.propTypes = {
  data: PropTypes.shape({
    org_id: PropTypes.number,
    org_name: PropTypes.string,
    available: PropTypes.bool,
    error: PropTypes.string,
    versions: PropTypes.arrayOf(
      PropTypes.shape({
        service_name: PropTypes.string,
        display_name: PropTypes.string,
        image_tag: PropTypes.string,
        updated_on: PropTypes.string,
      })
    ),
  }),
  loading: PropTypes.bool,
  error: PropTypes.string,
};

VersionStrip.defaultProps = {
  data: null,
  loading: false,
  error: "",
};

export default VersionStrip;
